import { useState } from 'react';
import { Container } from '@/components/Container';
import { AppLink } from '@/components/AppLink';
import { FilterPills } from '@/components/FilterPills';
import { CircularsList } from '@/sections/circulars/CircularsList';
import { FILTERS, matchesFilter, type CircularFilter } from '@/lib/circulars';
import type { Circular } from '@/lib/types';
import { EJournalCard } from './EJournalCard';

interface Props {
  status: 'loading' | 'error' | 'success';
  items: Circular[]; // newest first
  retry: () => void;
}

const HOME_LIMIT = 6;

// Two matching cards directly under the hero. Left: the notice board (every
// circular and notice). Right: the Student's E-Journal, and nothing else.
export function NoticesSection({ status, items, retry }: Props) {
  const [filter, setFilter] = useState<CircularFilter>('All');
  const shown = items.filter((c) => matchesFilter(c, filter)).slice(0, HOME_LIMIT);

  return (
    <section aria-labelledby="notices-title" className="border-t border-rule bg-paper">
      <Container className="flex flex-wrap gap-8 py-section-about">
        <div className="box-content flex min-w-0 flex-[1_1_560px] flex-col border border-rule bg-white p-7">
          <h2 id="notices-title" className="m-0 font-serif text-h2 font-semibold text-navy-900">
            Notice board
          </h2>
          <p className="mt-3 mb-0 max-w-[36em] text-[1.0625rem] leading-[1.5] text-ink-2">
            Circulars and official notices from ITES, newest first.
          </p>
          <FilterPills className="mt-6" label="Filter notices" options={FILTERS} value={filter} onChange={setFilter} />
          <CircularsList
            status={status}
            items={shown}
            totalCount={items.length}
            layout="compact"
            skeletonRows={6}
            onRetry={retry}
          />
          <div className="mt-auto pt-5">
            <AppLink
              href="/notices"
              className="block border-t border-rule pt-3.5 font-semibold text-teal-700 no-underline max-sm:min-h-11"
            >
              View all notices →
            </AppLink>
          </div>
        </div>
        <EJournalCard />
      </Container>
    </section>
  );
}
